import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import { Box, Container, Grid, Stack, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import WaveSurfer from "wavesurfer.js";
import VSNLoader from "../../shared/VSNLoader";
import VSNProgressiveImage from "../../shared/VSNProgressiveImage";
import VSNTypography from "../../shared/VSNTypography";
import videoImage from "../../assets/learning/video.webp";

import { LearningStyle } from "./LearningStyle";

const AudioPlayer = ({ data, loader }) => {
  const classes = LearningStyle();
  const theme = useTheme();
  const [transcript, setTranscript] = useState(false);

  const audioUrl = data?.link ?? "";

  const waveformRef = useRef(null);
  const wavesurfer = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    if (!waveformRef.current) return undefined;

    wavesurfer.current = WaveSurfer.create({
      container: waveformRef.current,
      waveColor: "#CBCBCF",
      progressColor: "#E87637",
      cursorColor: "transparent",
      height: 180,
      barWidth: 4,
      barRadius: 4,
      barAlign: "bottom",
      barGap: 5,
      responsive: true,
    });

    wavesurfer.current.load(audioUrl);

    return () => wavesurfer.current.destroy();
  }, [audioUrl]);

  const togglePlay = () => {
    wavesurfer.current.playPause();
    setIsPlaying((prev) => !prev);
  };

  const handleToggleTranscript = () => {
    setTranscript(!transcript);
  };

  if (loader) {
    return <VSNLoader variant="transParent" />;
  }

  return (
    <Container maxWidth="xl" p={{ sm: 4, xs: 2 }}>
      {data ? (
        <>
          <Grid container spacing={3} pt={3}>
            <Grid item xs={12} lg={4}>
              <Box
                position="relative"
                className={classes.audioImg}
                borderRadius={4}
                overflow="hidden"
              >
                <VSNProgressiveImage
                  placeholder={videoImage}
                  src={data?.thumbnail}
                  alt="Calm sleeping picture"
                  style={{ height: "100%", objectFit: "cover" }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} lg={8}>
              <Stack height="100%">
                <Stack direction={{ sm: "row" }}>
                  {audioUrl && waveformRef && (
                    <Box onClick={togglePlay}>
                      {isPlaying ? (
                        <PauseCircleIcon
                          sx={{
                            width: { sm: 80, xs: 60 },
                            height: { sm: 80, xs: 60 },
                            color: "orange.main",
                          }}
                        />
                      ) : (
                        <PlayCircleIcon
                          sx={{
                            width: { sm: 80, xs: 60 },
                            height: { sm: 80, xs: 60 },
                            color: "orange.main",
                          }}
                        />
                      )}
                    </Box>
                  )}
                  <Stack flex={1} ml={{ sm: 3 }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <VSNTypography
                        fontSize={{ md: 40, sm: 30, xs: 24 }}
                        fontWeight={700}
                      >
                        {data?.title ?? ""}
                      </VSNTypography>
                      {/* <IconButton className={classes.addIcon}>
                    <AddIcon fontSize="large" />
                  </IconButton> */}
                    </Stack>

                    <VSNTypography>
                      Instructor(s):{" "}
                      <VSNTypography
                        sx={{ fontWeight: "600" }}
                        component="strong"
                      >
                        {data?.instructor ?? ""}
                      </VSNTypography>
                    </VSNTypography>
                    <VSNTypography color="#898994">
                      {data?.content_duration ?? ""}
                    </VSNTypography>
                    <Stack
                      direction="row"
                      gap={1}
                      flexWrap="wrap"
                      alignItems="center"
                      pt={2}
                    >
                      <VSNTypography mr={2} sx={{ flex: "none" }}>
                        Topics Covered:
                      </VSNTypography>
                      <Stack
                        direction="row"
                        flexWrap="wrap"
                        gap={1.5}
                        flex={{ sm: 1 }}
                      >
                        {data?.tags?.map((tag, index) => (
                          <VSNTypography
                            key={index}
                            sx={{
                              padding: "4px 8px",
                              backgroundColor: "#FEF6EE",
                              borderRadius: "8px",
                              color: "var(--secondary)",
                            }}
                          >
                            {tag}
                          </VSNTypography>
                        ))}
                      </Stack>
                    </Stack>
                  </Stack>
                </Stack>
                <Stack
                  flex={1}
                  justifyContent="flex-end"
                  sx={{ borderBottom: `1px solid #898994`, pb: 1 }}
                  ref={waveformRef}
                />
              </Stack>
            </Grid>
          </Grid>
          <Stack
            direction={{ xts: "row-reverse" }}
            justifyContent="space-between"
            alignItems="center"
            gap={1}
            my={2}
          >
            {/* <VSNButton
          variant="outlined"
          sx={{ px: 2, flex: "none", width: { xts: "auto", xs: "100%" } }}
        >
          Mark As Complete
        </VSNButton> */}
            <VSNTypography
              variant="h6"
              className={classes.viewTranscript}
              onClick={handleToggleTranscript}
            >
              {!transcript ? (
                <>
                  View Transcript <KeyboardArrowDownIcon />
                </>
              ) : (
                <>
                  Close <KeyboardArrowUpIcon />
                </>
              )}
            </VSNTypography>
          </Stack>
          {transcript && (
            <Box className={classes.audioDescription}>
              <VSNTypography
                dangerouslySetInnerHTML={{
                  __html: data?.transcript,
                }}
              />
            </Box>
          )}
        </>
      ) : (
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          py={10}
        >
          No data found
        </Stack>
      )}
    </Container>
  );
};

export default AudioPlayer;
